import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiAlertCircle, FiX } from 'react-icons/fi';

const UnblockMemberDetailsModal = ({ member, onClose }) => {
    if (!member) return null;

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-SG');
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-SG', {
            style: 'currency',
            currency: 'SGD'
        }).format(value);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-3xl max-h-[80vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium">
                        {member.unblockID}
                        {member.TGContactUsername && ` - ${member.TGContactUsername}`}
                        {member.unblockName && ` - ${member.unblockName}`}
                    </h3>
                    <button 
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <FiX className="w-6 h-6" />
                    </button>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                        <p className="text-sm text-gray-500">Unblock ID</p>
                        <p className="font-medium">{member.unblockID}</p>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Category</p>
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            member.adsCategory === 'FL Platform' ? 'bg-pink-100 text-pink-800' :
                            member.adsCategory === 'Game' ? 'bg-purple-100 text-purple-800' :
                            member.adsCategory === 'Vape' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                        }`}>
                            {member.adsCategory}
                        </span>
                    </div>
                    <div>
                        <p className="text-sm text-gray-500">Status</p>
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            member.status === 'active' ? 'bg-green-100 text-green-800' :
                            member.status === 'expired' ? 'bg-red-100 text-red-800' :
                            member.status === 'expiring' ? 'bg-orange-100 text-orange-800' :
                            'bg-gray-100 text-gray-800'
                        }`}>
                            {member.status || 'expired'}
                        </span>
                    </div>
                    {member.TGContactUsername && (
                        <div>
                            <p className="text-sm text-gray-500">TG Contact</p>
                            <p className="font-medium">{member.TGContactUsername}</p>
                        </div>
                    )}
                    {member.unblockName && (
                        <div>
                            <p className="text-sm text-gray-500">Name</p>
                            <p className="font-medium">{member.unblockName}</p>
                        </div>
                    )}
                </div>

                <div className="border-t pt-4">
                    <h4 className="font-medium mb-2">Payment Statistics</h4>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-sm text-gray-500">Total Payments</p>
                            <p className="text-2xl font-bold text-green-600">
                                {formatCurrency(member.totalPayments)}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">Number of Payments</p>
                            <p className="text-2xl font-bold text-blue-600">
                                {member.paymentCount}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const UnblockMemberAnalytics = () => {
    const [topMembers, setTopMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedMember, setSelectedMember] = useState(null);

    useEffect(() => {
        const fetchAnalytics = async () => {
            try {
                setLoading(true);
                setError('');
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/unblockpayments/analytics/top-payments?limit=5`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );

                if (response.data.success) {
                    setTopMembers(response.data.data);
                }
            } catch (err) {
                console.error('Analytics fetch error:', err);
                setError(`Failed to fetch analytics: ${err.response?.data?.error || err.message}`);
            } finally {
                setLoading(false);
            }
        };

        fetchAnalytics();
    }, []);

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-SG', {
            style: 'currency',
            currency: 'SGD'
        }).format(value);
    };

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                <div className="animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                    <div className="space-y-3">
                        {[1, 2, 3, 4, 5].map((j) => (
                            <div key={j} className="flex justify-between items-center">
                                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mt-6 p-4 bg-red-50 rounded-lg flex items-center text-red-700">
                <FiAlertCircle className="w-5 h-5 mr-2" />
                {error}
            </div>
        );
    }

    const EmptyState = ({ title }) => (
        <div className="text-center py-4">
            <p className="text-gray-500">No {title} data available</p>
        </div>
    );

    return (
        <>
            <div className="bg-blue-50 rounded-lg shadow p-6">
                <h3 className="text-lg font-medium mb-4">Top 5 Members by Payment</h3>
                {topMembers.length > 0 ? (
                    <div className="space-y-3">
                        {topMembers.map((member, index) => (
                            <div
                                key={member._id}
                                onClick={() => setSelectedMember(member)}
                                className="flex justify-between items-center p-2 hover:bg-blue-100 rounded cursor-pointer transition duration-150"
                            >
                                <div className="flex items-center space-x-2 flex-1 mr-2">
                                    <span className="text-gray-800 truncate">
                                        {index + 1}. {member.unblockID}
                                        {member.TGContactUsername && ` - ${member.TGContactUsername}`}
                                        {member.unblockName && ` - ${member.unblockName}`}
                                    </span>
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full whitespace-nowrap
                                        ${member.adsCategory === 'FL Platform' ? 'bg-pink-100 text-pink-800' :
                                          member.adsCategory === 'Game' ? 'bg-purple-100 text-purple-800' :
                                          member.adsCategory === 'Vape' ? 'bg-green-100 text-green-800' :
                                          'bg-gray-100 text-gray-800'}`}>
                                        {member.adsCategory}
                                    </span>
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full whitespace-nowrap
                                        ${member.status === 'active' ? 'bg-green-100 text-green-800' : 
                                          member.status === 'expired' ? 'bg-red-100 text-red-800' :
                                          member.status === 'expiring' ? 'bg-orange-100 text-orange-800' :
                                          'bg-gray-100 text-gray-800'}`}>
                                        {member.status || 'expired'}
                                    </span>
                                </div>
                                <span className="font-medium text-blue-600 whitespace-nowrap">
                                    {formatCurrency(member.totalPayments)}
                                </span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <EmptyState title="member payment" />
                )}
            </div>

            {/* Member Details Modal */}
            {selectedMember && (
                <UnblockMemberDetailsModal
                    member={selectedMember}
                    onClose={() => setSelectedMember(null)}
                />
            )}
        </>
    );
};

export default UnblockMemberAnalytics;
